import React, { useContext, useEffect, useState } from "react";
import { Card, Form, Input, Button, Table, Select, Tag } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { HomeOutlined, ScheduleOutlined } from "@ant-design/icons";
import { getAllTickets } from "../../../network/network";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../context/AppContextProvider";
import {
  AddonName,
  TicketScanStatus,
  ticketStatus,
} from "../../../configs/constants";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { handleApiError } from "../../../utils/errorHandler";
import { getTableRowNumber } from "../../../utils/tablePagination";

const Ticketing = () => {
  const { authUser, appConfig } = useContext(AppContext);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [tablesData, setTablesData] = useState([]);
  const [paginationData, setPaginationData] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPageSize, setPerPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState([]);
  const [searchFormRef] = Form.useForm();
  let navigate = useNavigate();
  const { t } = useTranslation();

  const is_ticketing_addon_active = authUser.active_addons.some(
    (addon) => addon.id && addon.name === AddonName.Ticketing,
  );

  useEffect(() => {
    setIsDataLoading(true);
    getAllTickets({ ...searchParams, page: currentPage, per_page: perPageSize })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setTablesData(response.data.data);
        setPaginationData(response.data.pagination);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [searchParams, currentPage, perPageSize, t]);

  const handlePrintTicket = (item) => {
    navigate("/print-ticket", {
      state: { tickets: [item] },
    });
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "ticketing",
      breadcrumbName: t("tickets"),
      icon: <ScheduleOutlined />,
    },
  ];

  const ColumnsData = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <>{getTableRowNumber(index, paginationData)}</>;
      },
    },
    {
      title: t("name"),
      render: (record) => {
        return (
          <>
            {appConfig.direction === "ltr"
              ? record.product?.name_en || "-"
              : record.product?.name || "-"}
          </>
        );
      },
    },
    {
      title: t("quantity"),
      render: (record) => {
        const scanned = record.scan_quantity ?? 0;
        return (
          <Tag color="cyan">
            {scanned} / {record.total_quantity}
          </Tag>
        );
      },
    },
    {
      title: t("transaction_id"),
      dataIndex: "transaction",
      render: (transaction) => {
        return transaction?.uid || "-";
      },
    },
    {
      title: t("created_at"),
      dataIndex: "created_at",
      render: (created_at) => {
        return moment(created_at).format("MMM. D, YYYY hh:mm a");
      },
    },
    {
      title: t("expiry_date"),
      dataIndex: "expiry_date",
      render: (expiry_date) => {
        return moment(expiry_date).format("MMM. D, YYYY");
      },
    },
    {
      title: t("status"),
      render: (record) => {
        return (
          <>
            {record.status === TicketScanStatus.NotScanned && (
              <Tag color="blue">{t("not_scanned")}</Tag>
            )}
            {record.status === TicketScanStatus.PartiallyScanned && (
              <Tag color="orange">{t("partially_scanned")}</Tag>
            )}
            {record.status === TicketScanStatus.FullyScanned && (
              <Tag color="green">{t("fully_scanned")}</Tag>
            )}
            {record.status === TicketScanStatus.Expired && (
              <Tag color="error">{t("expired")}</Tag>
            )}
            {record.status === TicketScanStatus.Refunded && (
              <Tag color="var(--color-warning-hover)">
                {t("status_refunded")}
              </Tag>
            )}
          </>
        );
      },
    },
    {
      title: t("action"),
      align: "center",
      width: 130,
      render: (record) => {
        return (
          <>
            {is_ticketing_addon_active &&
              (record.status === TicketScanStatus.NotScanned ||
                record.status === TicketScanStatus.PartiallyScanned) && (
                <Button
                  type="primary"
                  onClick={() => handlePrintTicket(record)}
                >
                  {t("preview")}
                </Button>
              )}
          </>
        );
      },
    },
  ];

  const handleSearch = (values) => {
    let search_params = false;
    if (values.status) {
      search_params = {
        status: values.status,
      };
    }
    if (values.uid) {
      search_params = {
        uid: values.uid,
        ...search_params,
      };
    }

    if (search_params) {
      setSearchParams(search_params);
    } else {
      setSearchParams([]);
    }
  };

  const handleReset = () => {
    // Reset Form Fields
    searchFormRef.resetFields();
    // Reset Transactions List
    setSearchParams([]);
  };

  const searchForm = (
    <>
      <Form
        name="search-form"
        layout="inline"
        onFinish={handleSearch}
        form={searchFormRef}
      >
        <Form.Item name="uid" label={t("transaction_id")}>
          <Input allowClear />
        </Form.Item>
        <Form.Item name="status" label={t("status")}>
          <Select
            className="app-w-200px"
            options={ticketStatus.map((ticket) => ({
              label: t(ticket.text), // Assuming ticket.text is a key or actual text
              value: ticket.id,
            }))}
          />
        </Form.Item>
        <Form.Item>
          <Input
            type="submit"
            value={t("search")}
            className="app-search-submit"
          />
        </Form.Item>
        <Form.Item>
          <Input type="button" value={t("reset")} onClick={handleReset} />
        </Form.Item>
      </Form>
    </>
  );

  const handleTableChange = (pagination) => {
    setPaginationData(pagination);
    setCurrentPage(pagination.current);
    setPerPageSize(pagination.pageSize);
  };

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("tickets")}
          ghost={false}
          footer={searchForm}
        />
      </Card>
      <Card>
        <Table
          columns={ColumnsData}
          dataSource={tablesData}
          loading={isDataLoading}
          rowKey="id"
          pagination={{
            current: paginationData.current_page,
            pageSize: paginationData.per_page,
            total: paginationData.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </Card>
    </>
  );
};

export default Ticketing;
